/**
 * @file src/pages/company-onboarding-wizard/components/SkipCompanySetup.jsx
 * @description Confirmation panel shown when the user chooses to skip
 * company setup and go directly to the dashboard.
 *
 * What changed from the original:
 *  - No functional changes — logic was already correct
 *  - aria-live added to the warning panel for screen reader announcement
 *  - Button loading state handled via disabled prop from parent if needed
 *
 * Rules:
 *  - No state, no API calls — purely presentational
 *  - onSkip and onCancel are the only side effects, both from parent
 */

import React from "react";
import { motion } from "framer-motion";
import { Clock, AlertCircle, ArrowRight } from "lucide-react";
import Button from "components/ui/Button";

// ---------------------------------------------------------------------------
// SkipCompanySetup
// ---------------------------------------------------------------------------

/**
 * @param {{
 *   onSkip:   () => Promise<void>,
 *   onCancel: () => void,
 * }} props
 */
const SkipCompanySetup = ({ onSkip, onCancel }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: 12 }}
    transition={{ duration: 0.2 }}
    className="bg-warning-50 border border-warning-200 rounded-lg p-6 mt-6"
    aria-live="polite"
    role="region"
    aria-label="Skip company setup confirmation"
  >
    <div className="flex items-start gap-3">
      {/* Icon */}
      <div className="p-2 bg-warning-100 rounded-lg shrink-0">
        <Clock className="w-5 h-5 text-warning-600" />
      </div>

      <div className="flex-1">
        <h3 className="font-medium text-warning-800 mb-2">
          Skip Company Setup?
        </h3>

        <p className="text-sm text-warning-700 mb-4">
          You can explore the dashboard and complete your ZATCA onboarding
          later. However, you'll need to set up your company before accessing
          e-invoicing features.
        </p>

        {/* Limited access warning */}
        <div className="bg-warning-100 rounded-lg p-3 mb-4">
          <div className="flex items-center gap-2 mb-1">
            <AlertCircle className="w-4 h-4 text-warning-600 shrink-0" />
            <span className="text-sm font-medium text-warning-800">
              Limited Access Without Setup
            </span>
          </div>
          <p className="text-xs text-warning-700">
            Invoice clearance, AI validation, and VAT-compliant workflows will
            not be available until your company is configured.
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={onCancel}
            className="text-warning-700 border-warning-300 hover:bg-warning-100"
          >
            Continue Setup
          </Button>

          <Button
            onClick={onSkip}
            className="bg-warning-600 hover:bg-warning-700 text-white"
          >
            Skip for Now
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  </motion.div>
);

export default SkipCompanySetup;
